// Updated OrderAPIs.js
import { getToken } from "../utils/auth";
import { API_BASE_URL } from './URL_Header';


export const fetchOrderById = async (id) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/GetOrder/${id}`,
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`fetchOrderById failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to fetch order");
    }
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const createOrder = async (order) => {
  try {
    const response = await fetch(`${API_BASE_URL}/Order/MakeOrder`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(order),
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`createOrder failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to create order");
    }
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const updateOrder = async (id, order) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/EditOrder/${id}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(order),
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`updateOrder failed: Status ${response.status}, Response: ${errorText}`);
      let message = "فشل تحديث الطلب";
      try {
        const parsed = JSON.parse(errorText);
        message = parsed?.title || parsed?.error || parsed?.message || errorText || message;
      } catch {
        if (errorText?.trim()) message = errorText.trim();
      }
      throw new Error(message);
    }
    return response.status === 204 ? {} : await response.json();
  } catch (err) {
    throw new Error(err.message || "فشل تحديث الطلب");
  }
};

export const mergeOrders = async (sourceOrderId, otherOrderId, totalAmount) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/MergeOrders/${sourceOrderId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          otherOrderId: Number(otherOrderId),
          totalAmount: Number(totalAmount),
        }),
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`mergeOrders failed: Status ${response.status}, Response: ${errorText}`);
      let message = "فشل دمج الطلبات";
      try {
        const parsed = JSON.parse(errorText);
        message = parsed?.title || parsed?.error || parsed?.message || errorText || message;
      } catch {
        if (errorText?.trim()) message = errorText.trim();
      }
      throw new Error(message);
    }
    return response.status === 204 ? {} : await response.json();
  } catch (err) {
    throw new Error(err.message || "فشل دمج الطلبات");
  }
};

export const deleteOrder = async (id) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/DeleteOrder/${id}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`deleteOrder failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to delete order");
    }
    return response.status === 204 ? {} : await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};
//-----------------------------Unified Order APIs---------------------
const authHeaders = () => ({
  Authorization: `Bearer ${getToken()}`,
});

const buildOrdersUrl = (status, page, pageSize, date, followUpCheckpoint = null, sortOrder = null, teePlusStockStatus = null) => {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  if (status) params.set("status", status);
  if (date) params.set("date", date);
  if (followUpCheckpoint) params.set("followUpCheckpoint", followUpCheckpoint);
  if (sortOrder) params.set("sortOrder", sortOrder);
  if (teePlusStockStatus) params.set("teePlusStockStatus", teePlusStockStatus);
  return `${API_BASE_URL}/Order/Orders?${params.toString()}`;
};

/** Unified paginated order list — replaces all GetAll*Orders endpoints */
export const fetchOrders = async (status, page = 0, pageSize = 10, date = null, followUpCheckpoint = null, sortOrder = null, teePlusStockStatus = null) => {
  try {
    const response = await fetch(buildOrdersUrl(status, page, pageSize, date, followUpCheckpoint, sortOrder, teePlusStockStatus), {
      headers: authHeaders(),
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`fetchOrders(${status}) failed: ${response.status} - ${errorText}`);
      throw new Error(`Failed to fetch ${status || "all"} orders`);
    }
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

/** Unified summary — replaces all Get*OrdersSummary endpoints */
export const fetchOrdersSummary = async (status = null) => {
  try {
    const url = status
      ? `${API_BASE_URL}/Order/Summary?status=${encodeURIComponent(status)}`
      : `${API_BASE_URL}/Order/Summary`;
    const response = await fetch(url, { headers: authHeaders() });
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`fetchOrdersSummary(${status}) failed: ${response.status} - ${errorText}`);
      throw new Error("Failed to fetch orders summary");
    }
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

//-----------------------------Status wrappers (backward-compatible)---------------------
export const fetchNewOrders = (page = 0, pageSize = 10, followUpCheckpoint = null, sortOrder = "desc") =>
  fetchOrders("New", page, pageSize, null, followUpCheckpoint, sortOrder);

export const fetchFollowUpCheckpointCounts = async (status = "New") => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/FollowUpCheckpointCounts?status=${encodeURIComponent(status)}`,
      { headers: authHeaders() }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`fetchFollowUpCheckpointCounts failed: ${response.status} - ${errorText}`);
      throw new Error("Failed to fetch follow-up counts");
    }
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const fetchHoldOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Hold", page, pageSize);

export const fetchChangesOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Changes", page, pageSize);

export const fetchOldOrders = (page = 0, pageSize = 10, date = null) =>
  fetchOrders("Old", page, pageSize, date);

export const fetchSpamOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Spam", page, pageSize);

export const fetchConfirmedOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Confirmed", page, pageSize);

export const fetchGirabatcomOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Girabatcom", page, pageSize);

export const fetchPrintingHoldOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Printing Hold", page, pageSize);

export const fetchIncompleteOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Incomplete", page, pageSize);

export const fetchPrintedOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Printed", page, pageSize);

export const fetchPrintletWorkflowOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Printlet", page, pageSize);

export const fetchTeePlusOrders = (page = 0, pageSize = 10, teePlusStockStatus = null) =>
  fetchOrders("TeePlus", page, pageSize, null, null, null, teePlusStockStatus);

export const fetchTeePlusStock = async () => {
  const response = await fetch(`${API_BASE_URL}/TeePlusStock`, { headers: authHeaders() });
  const text = await response.text();
  if (!response.ok) throw new Error(text || "Stock Check Failed");
  return text ? JSON.parse(text) : {};
};

export const refreshTeePlusStock = async () => {
  const response = await fetch(`${API_BASE_URL}/TeePlusStock/refresh`, {
    method: "POST",
    headers: authHeaders(),
  });
  const text = await response.text();
  if (!response.ok) throw new Error(text || "Stock Check Failed");
  return text ? JSON.parse(text) : {};
};

//-----------------------------Purchase wrappers---------------------
export const fetchUnderPurchaseOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Under Purchase", page, pageSize);

export const fetchWaitingConfirmingOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Waiting Confirming", page, pageSize);

export const fetchCanceledOrders = (page = 0, pageSize = 10, sortOrder = "desc") =>
  fetchOrders("Canceled", page, pageSize, null, null, sortOrder);

export const fetchCancelledByCustomerOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Cancelled by Customer", page, pageSize);

export const fetchConfirmingOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Confirming", page, pageSize);

export const fetchDepositRequiredOrders = (page = 0, pageSize = 10) =>
  fetchOrders("Deposit", page, pageSize);

export const fetchCustomOrderRequests = (page = 0, pageSize = 10) =>
  fetchOrders("Custom", page, pageSize);
//------------------------------Totals & Summaries--------------------
export const fetchUniqueOldDates = async () => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/GetUniqueOldDates`,
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`fetchUniqueOldDates failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to fetch unique old dates");
    }
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const fetchNewOrdersTotals = async () => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/GetNewOrdersTotals`,
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`fetchNewOrdersTotals failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to fetch new orders totals");
    }
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const fetchHoldOrdersTotals = async () => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/GetHoldOrdersTotals`,
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`fetchHoldOrdersTotals failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to fetch hold orders totals");
    }
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const fetchChangesOrdersTotals = async () => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/GetChangesOrdersTotals`,
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`fetchChangesOrdersTotals failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to fetch changes orders totals");
    }
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const fetchOldOrdersTotals = async (date = null) => {
  try {
    let url = `${API_BASE_URL}/Order/GetOldOrdersTotals`;
    if (date) {
      url += `?date=${encodeURIComponent(date)}`;
    }
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`fetchOldOrdersTotals failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to fetch old orders totals");
    }
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const fetchNewOrdersSummary = () => fetchOrdersSummary("New");
export const fetchConfirmedOrdersSummary = () => fetchOrdersSummary("Confirmed");
export const fetchGirabatcomOrdersSummary = () => fetchOrdersSummary("Girabatcom");
export const fetchPrintingHoldOrdersSummary = () => fetchOrdersSummary("Printing Hold");
export const fetchIncompleteOrdersSummary = () => fetchOrdersSummary("Incomplete");
export const fetchPrintedOrdersSummary = () => fetchOrdersSummary("Printed");
export const fetchPrintletWorkflowOrdersSummary = () => fetchOrdersSummary("Printlet");
export const fetchTeePlusOrdersSummary = () => fetchOrdersSummary("TeePlus");
export const fetchCanceledOrdersSummary = () => fetchOrdersSummary("Canceled");
export const fetchHoldOrdersSummary = () => fetchOrdersSummary("Hold");
export const fetchChangesOrdersSummary = () => fetchOrdersSummary("Changes");
export const fetchOldOrdersSummary = () => fetchOrdersSummary("Old");
export const fetchDepositRequiredSummary = () => fetchOrdersSummary("Deposit");
export const fetchCustomOrdersSummary = () => fetchOrdersSummary("Custom");

export const deleteAllNewOrders = async () => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/DeleteAllNewOrders`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`deleteAllNewOrders failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to delete all new orders");
    }
    return response.status === 204 ? {} : await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const deleteAllOldOrders = async () => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/DeleteAllOldOrders`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`deleteAllOldOrders failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to delete all old orders");
    }
    return response.status === 204 ? {} : await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const updateFollowUpCheckpoint = async (id, checkpoint) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/UpdateFollowUpCheckpoint/${id}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ checkpoint, Checkpoint: checkpoint }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`updateFollowUpCheckpoint failed: ${response.status} - ${errorText}`);
      throw new Error("فشل تحديث مرحلة المتابعة");
    }

    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

// api/OrderAPIs.js
export const updateOrderStatus = async (id, status, reason = null, fullNote = null, extra = {}) => {
  try {
    const payload = { status };
    
    // إذا كان هناك سبب، نضيفه
    if (reason && reason.trim()) {
      payload.reason = reason.trim();
    }

    // إذا كان هناك note كاملة، نرسلها
    if (fullNote !== null) {
      payload.note = fullNote;
    }

    if (extra.paidAmount != null && extra.paidAmount !== "") {
      payload.paidAmount = Number(extra.paidAmount);
    }

    const response = await fetch(
      `${API_BASE_URL}/Order/UpdateOrderStatus/${id}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`updateOrderStatus failed: Status ${response.status}, Response: ${errorText}`);
      let message = "فشل تحديث حالة الطلب";
      if (errorText) {
        try {
          const parsed = JSON.parse(errorText);
          if (typeof parsed === "string" && parsed.trim()) message = parsed.trim();
          else if (typeof parsed?.message === "string" && parsed.message.trim()) message = parsed.message.trim();
          else if (typeof parsed?.title === "string" && parsed.title.trim()) message = parsed.title.trim();
        } catch {
          const plain = errorText.trim();
          if (plain && plain.length < 400) message = plain;
        }
      }
      throw new Error(message);
    }

    return await response.json();
  } catch (err) {
    throw new Error(err.message || "حدث خطأ أثناء التحديث");
  }
};

export const updateOrderStatusBulk = async (orderIds, status, reason = null) => {
  const payload = { orderIds, status };
  if (reason && String(reason).trim()) payload.reason = String(reason).trim();

  const response = await fetch(`${API_BASE_URL}/Order/UpdateOrderStatusBulk`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error(`updateOrderStatusBulk failed: Status ${response.status}, Response: ${errorText}`);
    throw new Error("فشل التحديث الجماعي لحالة الطلبات");
  }

  return await response.json();
};

export const printletWorkflowBulkSend = async (orderIds, confirmDuplicateIds = []) => {
  const ids = (orderIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
  const confirmIds = (confirmDuplicateIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
  if (ids.length === 0) {
    throw new Error("لا يوجد رقم طلب صالح للإرسال");
  }

  const response = await fetch(`${API_BASE_URL}/Order/PrintletBulkSend`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify({
      orderIds: ids,
      confirmDuplicateIds: confirmIds,
    }),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || data.message || "فشل الإرسال الجماعي إلى Printlet");
  }
  return data;
};

export const markOrderSentToFincart = async (id, sentToFincart = true, extra = {}) => {
  const response = await fetch(
    `${API_BASE_URL}/Order/MarkSentToFincart/${id}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({
        sentToFincart,
        shippingProvider: extra.shippingProvider || "Fincart",
        shippingOrderId: extra.shippingOrderId || extra.fincartOrderId || null,
        trackingNumber: extra.trackingNumber || null,
      }),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    console.error(`markOrderSentToFincart failed: Status ${response.status}, Response: ${errorText}`);
    throw new Error("فشل تحديث حالة الإرسال لـ Fincart");
  }

  return await response.json();
};

export const markOrdersSentToFincartBulk = async (orderIds, sentToFincart = true, items) => {
  const response = await fetch(
    `${API_BASE_URL}/Order/MarkSentToFincartBulk`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({
        orderIds,
        sentToFincart,
        shippingProvider: "Fincart",
        items: items || [],
      }),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    console.error(`markOrdersSentToFincartBulk failed: Status ${response.status}, Response: ${errorText}`);
    throw new Error("فشل تحديث حالة الإرسال لـ Fincart");
  }

  return await response.json();
};

export const revertToNew = async (id) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/RevertToNew/${id}`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`revertToNew failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to revert order to new");
    }
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const searchOrdersByPhone = async (phone) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/SearchOrdersByPhone?phone=${encodeURIComponent(phone)}`,
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`searchOrdersByPhone failed: Status ${response.status}, Response: ${errorText}`);
      throw new Error("Failed to search orders by phone");
    }
    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const checkDuplicatePhoneCount = async (currentOrderId, phone1, phone2) => {
  try {
    const params = new URLSearchParams();
    // Omit a blank phone. phone2= matches every order stored with an empty second phone.
    if (phone1) params.set("phone1", phone1);
    if (phone2) params.set("phone2", phone2);
    const response = await fetch(
      `${API_BASE_URL}/Order/CheckDuplicatePhoneCount/${currentOrderId}?${params}`,
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error("فشل في التحقق من تكرار الرقم");
    }

    return await response.json(); // يرجع عدد الطلبات
  } catch (err) {
    throw new Error(err.message);
  }
};

export const sendSecondaryPhoneConfirmation = async (orderId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/SendSecondaryPhoneConfirmation/${orderId}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || data.message || "Failed to send secondary phone confirmation");
    }

    return data;
  } catch (err) {
    throw new Error(err.message);
  }
};

export const sendPrimaryPhoneConfirmation = async (orderId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/SendPrimaryPhoneConfirmation/${orderId}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || data.message || "Failed to send primary phone confirmation");
    }

    return data;
  } catch (err) {
    throw new Error(err.message);
  }
};

export const sendPrimaryPhoneManualTemplate = async (orderId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/SendPrimaryPhoneManualTemplate/${orderId}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || data.message || "Failed to send template to primary phone");
    }

    return data;
  } catch (err) {
    throw new Error(err.message);
  }
};

export const sendDepositInfoTemplate = async (orderId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/SendDepositInfoTemplate/${orderId}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || data.message || "Failed to send deposit info template");
    }

    return data;
  } catch (err) {
    throw new Error(err.message);
  }
};

export const sendCompleteOrderPromptTemplate = async (orderId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/SendCompleteOrderPromptTemplate/${orderId}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || data.message || "Failed to send complete order prompt template");
    }

    return data;
  } catch (err) {
    throw new Error(err.message);
  }
};

export const processFollowUpPhones = async (phoneNumbers) => {
  try {
    const response = await fetch(`${API_BASE_URL}/Order/ProcessFollowUpPhones`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({ phoneNumbers }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to process follow-up phones");
    }

    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const fetchBulkWhatsAppTemplates = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/Order/BulkWhatsAppTemplates`, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to load WhatsApp templates");
    }

    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const addBulkWhatsAppTemplate = async ({ templateId, name, description }) => {
  try {
    const response = await fetch(`${API_BASE_URL}/Order/BulkWhatsAppTemplates`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({ templateId, name, description }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to add template");
    }

    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

export const deleteBulkWhatsAppTemplate = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/Order/BulkWhatsAppTemplates/${id}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to delete template");
    }
  } catch (err) {
    throw new Error(err.message);
  }
};

export const sendBulkWhatsAppTemplate = async (templateId, phoneNumbers, orderId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/Order/SendBulkWhatsAppTemplate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({
        templateId,
        phoneNumbers,
        orderId: orderId == null || orderId === "" ? undefined : String(orderId),
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to send bulk WhatsApp template");
    }

    return await response.json();
  } catch (err) {
    throw new Error(err.message);
  }
};

/** BeOn template sent_to_cart — notified when order is handed to Fincart. */
export const SENT_TO_CART_TEMPLATE_ID = 83692;

/**
 * Notify customer(s) that their order was sent to the shipping company (Fincart).
 * @param {string|string[]} phoneNumbers - primary phone(s); array or newline-separated string
 */
export const sendSentToCartWhatsApp = async (phoneNumbers) => {
  const phones = Array.isArray(phoneNumbers)
    ? phoneNumbers
        .map((p) => String(p || "").trim())
        .filter((p) => p && p !== "غير معروف")
        .join("\n")
    : String(phoneNumbers || "").trim();

  if (!phones) return null;
  return sendBulkWhatsAppTemplate(SENT_TO_CART_TEMPLATE_ID, phones);
};

/**
 * Fetch BeOn conversation for an order (backend resolves/caches conversationId).
 * @param {number|string} orderId
 * @param {{ refresh?: boolean, phone?: string }} [options]
 */
export const fetchOrderConversation = async (orderId, options = {}) => {
  try {
    const params = new URLSearchParams();
    params.set("refresh", options.refresh ? "true" : "false");
    if (options.phone) params.set("phone", options.phone);

    const response = await fetch(
      `${API_BASE_URL}/Order/Conversation/${orderId}?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data?.error || data?.Error || `Failed to load conversation (${response.status})`);
    }

    return data;
  } catch (err) {
    throw new Error(err.message || "Failed to load conversation");
  }
};

export const fetchOrderStatusHistory = async (id) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/StatusHistory/${id}`,
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data?.error || data?.title || `فشل تحميل سجل التحديثات (${response.status})`);
    }

    return data;
  } catch (err) {
    throw new Error(err.message || "فشل تحميل سجل التحديثات");
  }
};

export const fetchOrderProductionHistory = async (id) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/Order/ProductionHistory/${id}`,
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data?.error || data?.title || `فشل تحميل سجل الإنتاج (${response.status})`);
    }

    return data;
  } catch (err) {
    throw new Error(err.message || "فشل تحميل سجل الإنتاج");
  }
};

export const assignProductionSource = async (orderId, source, reason = null) => {
  const payload = { orderId, source };
  if (reason && String(reason).trim()) payload.reason = String(reason).trim();

  const response = await fetch(`${API_BASE_URL}/Order/AssignProductionSource`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error || data?.Error || "فشل تعيين مصدر الإنتاج");
  }
  return data;
};

export const assignProductionSourceBulk = async (orderIds, source, reason = null) => {
  const payload = { orderIds, source };
  if (reason && String(reason).trim()) payload.reason = String(reason).trim();

  const response = await fetch(`${API_BASE_URL}/Order/AssignProductionSourceBulk`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error || data?.Error || "فشل التعيين الجماعي لمصدر الإنتاج");
  }
  return data;
};
